import { useEffect, useState } from "react";
import Select, { components } from "react-select";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";
import { Breadcrumbs, DebitsTable, FormWrapper, SubmitButton } from "../components";
import { useAgent, useApiService, usePayments } from "../hooks";

const Option = (props) => {
  return (
    <div>
      <components.Option {...props}>
        <div className="row">
          <span>{props.label}</span>
          <span className="small gray">ID: {props.data.value}</span>
        </div>
      </components.Option>
    </div>
  );
};

const SingleValue = (props) => {
  return (
    <components.SingleValue {...props}>
      <div className="row">
        <span>{props.data.label}</span>
        <span className="small gray">ID:{props.data.value}</span>
      </div>
    </components.SingleValue>
  );
};

const ProjectOption = (props) => {
  return (
    <div>
      <components.Option {...props}>
        <div className="row">
          <span className="value">{props.label}</span>
          <span className="small gray">{props.data.company_name}</span>
        </div>
      </components.Option>
    </div>
  );
};

const ProjectSingleValue = (props) => {
  return (
    <components.SingleValue {...props}>
      <div className="row">
        <span className="value">{props.data.label}</span>
      </div>
    </components.SingleValue>
  );
};

function DebitsPage() {
  const tableColumns = [
    { id: "agent_name", name: "Agent Name" },
    { id: "agent_id", name: "Agent ID" },
    { id: "project_name", name: "Project Name" },
    { id: "commission", name: "Commission" },
    { id: "payment_method", name: "Payment Method" },
    { id: "reference_number", name: "Reference Number" },
    { id: "action", name: "Attach Screenshot" },
  ];

  const [debits, setDebits] = useState([]);
  const { fetchDebits } = usePayments();

  const fetchDebitsData = async () => {
    const { payments, error } = await fetchDebits();
    if (error) return toast.error();
    setDebits(payments);
  };
  useEffect(() => {
    fetchDebitsData();
  }, []);

  const initialFormData = {
    agent_id: "",
    agent_name: "",
    project_name: "",
    bank_account_number: "",
    bank_ifsc: "",
    re_enter_bank_account_number: "",
    name_in_bank: "",
    commission_amount: "",
    payment_method: "",
    note: "",
    project_id: "",
    reference_number: "",
    payment_type: "commission",
  };
  const [formData, setFormData] = useState(initialFormData);
  const [agentCommission, setAgentCommission] = useState("");
  const { getAllAgentNamesWithId, fetchAgentDetails, getProjectsByAgentId, getAgentCommissionForProject } = useAgent();
  const { post } = useApiService();
  const handlePayment = async (e) => {
    e.preventDefault();
    if (!selectedPaymentMethod) return toast.error("Please select a payment method.");

    const dataToSubmit = {
      status: "completed",
      amount: `${formData.commission_amount}`,
      amount_type: "debit",
      recipient: formData.name_in_bank,
      payee: "Aurus Group",
      payment_method: selectedPaymentMethod.value,
      agent_id: formData.agent_id,
      project_name: formData.project_name,
      project_id: formData.project_id,
      agent_name: formData.agent_name,
      reference_number: formData.reference_number,
      note: formData.note,
      payment_type: formData.payment_type,
    };
    submitPayment(dataToSubmit);
  };
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  const submitPayment = async (dataToSubmit) => {
    try {
      const response = await post("/api/payment", dataToSubmit);
      if (response.status) {
        toast.success("Payment added successfully.");
      } else {
        throw new Error("Error adding payment.");
      }
    } catch (error) {
      toast.error("Error adding payment.");
      return;
    }
    setFormData(initialFormData);
    setSelectedAgent(null);
    setProjectsList([]);
    setSelectedProject(null);
    await fetchDebitsData();
  };

  const [agentsList, setAgentsList] = useState([]);
  useEffect(() => {
    const getAgentsList = async () => {
      const { agents, error } = await getAllAgentNamesWithId();
      if (error) return toast.error(error);
      setAgentsList(agents.map((agent) => ({ value: agent.id, label: agent.name })));
    };
    getAgentsList();
  }, []);

  const handleAgentIdChange = async (e) => {
    const { value } = e.target;
    const selectedAgentOption = agentsList.find((option) => option.value === Number(value));
    if (selectedAgentOption) {
      handleAgentSelect(selectedAgentOption);
    } else {
      const resetData = {
        agent_id: value,
        agent_name: "",
        bank_account_number: "",
        bank_ifsc: "",
        re_enter_bank_account_number: "",
        name_in_bank: "",
      };
      setSelectedAgent(null);
      setFormData({ ...formData, ...resetData });
    }
  };

  const commonPaymentMethods = [
    { value: "Cash", label: "Cash" },
    { value: "UPI", label: "UPI" },
    { value: "IMPS", label: "IMPS" },
    { value: "NEFT", label: "NEFT" },
    { value: "RTGS", label: "RTGS" },
    { value: "Cheque", label: "Cheque" },
    { value: "DD", label: "DD" },
  ];

  const [projectsList, setProjectsList] = useState([]);
  const getProjectsForAgent = async () => {
    toast.dismiss();
    if (!formData.agent_id) return toast.error("Please select an agent first.");
    const { projects } = await getProjectsByAgentId(formData.agent_id);
    if (!projects || projects.length === 0) return;
    setProjectsList(projects);
  };

  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const handleAgentSelect = async (selectedOption) => {
    const { agent, error } = await fetchAgentDetails(selectedOption.value);
    if (error) return toast.error(error);
    const { agent: details, bank } = agent;
    const agentData = {
      agent_id: details.id,
      agent_name: details.name ?? "",
      bank_account_number: bank.bank_account_number ?? "",
      bank_ifsc: bank.bank_ifsc ?? "",
      name_in_bank: bank.name_in_bank ?? "",
      re_enter_bank_account_number: bank.bank_account_number ?? "",
    };
    setSelectedAgent(selectedOption);
    setSelectedProject(null);
    setFormData((prev) => ({ ...prev, ...agentData }));
  };

  const handleInputChange = (e, useTextCase) => {
    const { name, value } = e.target;
    let newValue = value;
    if (useTextCase === "uppercase") newValue = value.toUpperCase();
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleProjectSelect = async (selectedOption) => {
    setSelectedProject(selectedOption);
    const { commission, error } = await getAgentCommissionForProject(selectedOption.value, formData.agent_id);
    if (error) return toast.error(error);
    const commissionValue = parseInt(
      commission.total_commission_withdrawable.replaceAll(",", "").replaceAll(".00", "")
    );
    setAgentCommission(commissionValue);
    setFormData((prev) => ({
      ...prev,
      project_name: selectedOption.label,
      project_id: selectedOption.value,
      commission_amount: commissionValue,
    }));
  };

  return (
    <>
      <Breadcrumbs />
      <DebitsTable columns={tableColumns} data={debits} title="Agent Commission Paid" />
      <section className="section_wrapper">
        <div className="section section_title_wrapper">
          <h2 className="section_title">Make a Payment</h2>
        </div>
        <FormWrapper id="payment_form" onSubmit={handlePayment} className="form-w-75">
          <div className="section_title">Enter Recipient Details</div>
          <div className="column">
            <input
              name="agent_id"
              type="number"
              className="input"
              placeholder="Agent ID"
              required
              value={formData.agent_id}
              onChange={handleAgentIdChange}
              min={0}
            />
            <Select
              options={agentsList}
              name="agent_name"
              placeholder="Agent Name"
              required
              className="select"
              isSearchable
              onChange={handleAgentSelect}
              value={selectedAgent}
              components={{ Option, SingleValue }}
              styles={{
                control: (base) => ({
                  ...base,
                  border: "1px solid var(--gray-light)",
                  boxShadow: "none",
                  ":hover": {
                    border: "1px solid var(--gray-light)",
                  },
                }),
              }}
            />
            <Select
              name="project_name"
              options={projectsList}
              onMenuOpen={getProjectsForAgent}
              placeholder="Project Name"
              loadingMessage="Loading projects..."
              required
              className="select"
              isSearchable
              onChange={handleProjectSelect}
              value={selectedProject}
              components={{ Option: ProjectOption, SingleValue: ProjectSingleValue }}
              styles={{
                control: (base) => ({
                  ...base,
                  border: "1px solid var(--gray-light)",
                  boxShadow: "none",
                  ":hover": {
                    border: "1px solid var(--gray-light)",
                  },
                }),
              }}
            />
            <input
              type="text"
              name="bank_account_number"
              className="input"
              placeholder="Bank Account Number"
              required
              value={formData.bank_account_number}
              onChange={handleInputChange}
              maxLength={20}
            />
            <input
              type="text"
              name="bank_ifsc"
              className="input"
              placeholder="IFSC Code"
              required
              value={formData.bank_ifsc}
              onChange={handleInputChange}
              maxLength={11}
            />
            <input
              type="text"
              name="re_enter_bank_account_number"
              className="input"
              placeholder="Re-enter Bank Account Number"
              required
              value={formData.re_enter_bank_account_number}
              onChange={handleInputChange}
              maxLength={20}
            />
            <input
              type="text"
              name="name_in_bank"
              className="input"
              placeholder="Receiver's Name"
              value={formData.name_in_bank}
              required
              onChange={handleInputChange}
              maxLength={50}
            />
            <div className="input_with_prefix_wrapper">
              <span className="prefix">₹</span>
              <input
                type="number"
                name="commission_amount"
                className="input"
                placeholder="Commission Amount (₹)"
                required
                value={formData.commission_amount}
                onChange={handleInputChange}
                min={0}
                max={agentCommission}
              />
            </div>
            <CreatableSelect
              options={commonPaymentMethods}
              name="payment_method"
              placeholder="Method of payment"
              required
              className="select"
              isSearchable
              onChange={(selectedOption) => setSelectedPaymentMethod(selectedOption)}
              value={selectedPaymentMethod}
              onCreateOption={(inputValue) => setSelectedPaymentMethod({ value: inputValue, label: inputValue })}
              styles={{
                control: (base) => ({
                  ...base,
                  border: "1px solid var(--gray-light)",
                  boxShadow: "none",
                  ":hover": {
                    border: "1px solid var(--gray-light)",
                  },
                }),
              }}
            />

            <input
              type="text"
              name="reference_number"
              className="input"
              placeholder="Reference Number"
              required
              value={formData.reference_number}
              onChange={(e) => handleInputChange(e, "uppercase")}
              maxLength={20}
            />
            <textarea
              name="note"
              className="input_textarea"
              placeholder="Note/Comment"
              onChange={handleInputChange}
              value={formData.note}
            />
          </div>
          <SubmitButton form="payment_form" start>
            Confirm
          </SubmitButton>
        </FormWrapper>
      </section>
    </>
  );
}

export default DebitsPage;
